import { TCustomerUserDetailDto } from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { useMyProductContracts } from "@/api/services/main/product-contract";
import { Checkbox } from "@/components/elements/Checkbox";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { InnerBox } from "@/components/elements/InnerBox";
import { Table } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import { text } from "@/theme/colors";
import { EProductCode } from "@/types/enum";
import { formatDateOrDash } from "@/utils/dayjs";
import { hasActiveProduct } from "@/features/customer-user/utils/productAccess";
import { productAccessSortableHeader } from "@/features/customer-user/components/productAccessSortableHeader";
import { useSortedProductAccess } from "@/features/customer-user/hooks/useSortedProductAccess";
import { Box, Stack, Typography, styled } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";

type TProductAccessRow = {
  id: number;
  productCode: string;
  productName: string;
  activeUserCount: string;
  lastLoginAt: string;
  startedAt: string;
  hasAccess: boolean;
};

const readonlyCheckboxSx = { pointerEvents: "none", ml: "0 !important" } as const;

export const ProductAccessSection: FC<{ user: TCustomerUserDetailDto }> = ({ user }) => {
  const { data: contracts } = useMyProductContracts();

  const accessByCode = useMemo(() => new Map(user.productAccess.map((item) => [item.productCode as string, item])), [user.productAccess]);

  const rows = useMemo<TProductAccessRow[]>(
    () =>
      contracts.map((c) => {
        const access = accessByCode.get(c.product.code);
        return {
          id: c.id,
          productCode: c.product.code,
          productName: c.product.name,
          activeUserCount: String(c.activeUserCount),
          lastLoginAt: formatDateOrDash(access?.lastLoginAt),
          startedAt: formatDateOrDash(access?.createdAt),
          hasAccess: hasActiveProduct(user.productAccess, c.product.code as EProductCode),
        };
      }),
    [contracts, user.productAccess, accessByCode],
  );

  const sortedRows = useSortedProductAccess(rows);

  const allChecked = rows.length > 0 && rows.every((r) => r.hasAccess);
  const someChecked = rows.some((r) => r.hasAccess);

  const columns = useMemo<MRT_ColumnDef<TProductAccessRow>[]>(
    () => [
      {
        header: "",
        id: "access",
        size: 48,
        Header: () => <Checkbox nonControl checked={allChecked} indeterminate={!allChecked && someChecked} disabled sx={readonlyCheckboxSx} />,
        Cell: ({ row }) => <Checkbox nonControl checked={row.original.hasAccess} disabled sx={readonlyCheckboxSx} />,
      },
      {
        header: "プロダクト名",
        id: "productName",
        accessorKey: "productName",
        size: 300,
        Header: productAccessSortableHeader("productName", "プロダクト名"),
        Cell: ({ row }) => <EllipsisText title={row.original.productName} variant="body14" maxWidth={284} />,
      },
      { header: "有効ユーザー数", id: "activeUserCount", accessorKey: "activeUserCount" },
      { header: "最終ログイン", id: "lastLoginAt", accessorKey: "lastLoginAt", Header: productAccessSortableHeader("lastLoginAt", "最終ログイン") },
      { header: "ご利用開始日", id: "startedAt", accessorKey: "startedAt", Header: productAccessSortableHeader("startedAt", "ご利用開始日") },
    ],
    [allChecked, someChecked],
  );

  const { table } = useTable<TProductAccessRow>({ columns, rows: sortedRows, getRowId: (row) => String(row.id) });

  return (
    <InnerBox title="プロダクトアクセス権限" padding="24px">
      <Stack gap="16px">
        <CompactHeaderTable>
          <Table table={table} />
        </CompactHeaderTable>
        <Typography variant="cap12" color={text.secondary}>
          プロダクトのご契約変更については、右下のチャットよりお問い合わせください。
        </Typography>
      </Stack>
    </InnerBox>
  );
};

const CompactHeaderTable = styled(Box)`
  .MuiTableCell-head {
    font-size: 14px;
  }
`;
